import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../../lib/api'
import type { TimeEntry } from './types'
import styles from './TimerWidget.module.css'

export const TIMER_EVENT = 'pm-timer-changed'
export function notifyTimerChanged() { window.dispatchEvent(new Event(TIMER_EVENT)) }

function elapsedLabel(startIso: string): string {
  const start = new Date(startIso).getTime()
  const secs = Math.max(0, Math.floor((Date.now() - start) / 1000))
  const h = Math.floor(secs / 3600)
  const m = Math.floor((secs % 3600) / 60)
  const s = secs % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  return h ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`
}

/** Compact running-timer indicator for the app topbar. Polls /timer/current. */
export default function TimerWidget() {
  const [timer, setTimer] = useState<TimeEntry | null>(null)
  const [, force] = useState(0)
  const tick = useRef<number | null>(null)

  const refresh = useCallback(async () => {
    try { setTimer(await api.get<TimeEntry | null>('/api/project-mgmt/timer/current')) }
    catch { /* not authorized / offline — hide */ }
  }, [])

  useEffect(() => {
    refresh()
    const onChange = () => refresh()
    window.addEventListener(TIMER_EVENT, onChange)
    return () => window.removeEventListener(TIMER_EVENT, onChange)
  }, [refresh])

  // 1s ticker only while a timer is running
  useEffect(() => {
    if (timer?.running) {
      tick.current = window.setInterval(() => force(n => n + 1), 1000)
      return () => { if (tick.current) window.clearInterval(tick.current) }
    }
  }, [timer?.running])

  async function stop() {
    try { await api.post('/api/project-mgmt/timer/stop'); setTimer(null); notifyTimerChanged() }
    catch { /* noop */ }
  }

  if (!timer?.running || !timer.started_at) return null

  return (
    <div className={styles.widget} title={timer.description || 'Running timer'}>
      <span className={styles.dot} />
      <span className={styles.time}>{elapsedLabel(timer.started_at)}</span>
      <button className={styles.stop} onClick={stop} aria-label="Stop timer">■</button>
    </div>
  )
}
